/**
 * EVERY PLAYER AT THE TABLE, A GIFT FOR THE NEXT VISIT.
 *
 * The table still plays as one: one person at the registered table scratches
 * and the immediate in-house prize belongs to the table. With this on, every
 * OTHER registered player at that table also gets their own separate roll for a
 * gift good on a later visit, banked on their own profile.
 *
 * The prizes in play are the ones already marked "use on a later visit" in the
 * prize list — nothing new to set up. With none marked, nobody gets anything
 * extra and the line below says so. One tap each way.
 */
import { useCallback, useEffect, useState } from 'react';
import { Gift, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '../../../lib/api';

export const GoalTableExtrasBar = ({ organizationId }) => {
    const { t } = useTranslation();
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);
    const x = (k, o) => t(`goalGame.tableExtras.${k}`, o);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const res = await api.get('/goal-game/admin/table-extras',
                { params: { organization_id: organizationId } });
            setCfg(res.data);
        } catch (_e) { setCfg(null); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const flip = async () => {
        if (busy || !cfg) return;
        setBusy(true);
        try {
            const res = await api.put('/goal-game/admin/table-extras',
                { organization_id: organizationId, on: !cfg.on });
            setCfg(res.data);
            toast.success(res.data.on ? x('turnedOn') : x('turnedOff'));
        } catch (_e) { toast.error(x('failed')); } finally { setBusy(false); }
    };

    if (!cfg) return null;
    const prizes = cfg.prizes || [];

    return (
        <div data-testid="gg-table-extras"
             className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
            <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" />{x('title')}
            </span>
            <button type="button" onClick={flip} disabled={busy} aria-pressed={!!cfg.on}
                    data-testid="gg-table-extras-switch"
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${
                        cfg.on ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white dark:bg-slate-900 text-muted-foreground border-slate-300 dark:border-slate-700'}`}>
                {busy && <Loader2 size={13} className="animate-spin" />}
                {cfg.on ? x('on') : x('off')}
            </button>
            <span className="text-xs text-muted-foreground" data-testid="gg-table-extras-prizes">
                {prizes.length === 0
                    ? x('noPrizes')
                    : x('prizeCount', { count: prizes.length,
                                        list: prizes.map((p) => p.title).join(', ') })}
            </span>
        </div>
    );
};

export default GoalTableExtrasBar;
